import { __rest, __assign } from 'tslib';
import React, { forwardRef } from 'react';
import { useIntl } from 'react-intl';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { faExclamationCircle, faInfoCircle, faCheckCircle, faExclamationTriangle, faSpinnerThird, faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import '@popperjs/core';
import { FaIcon } from './FaIcon.esm.js';

/**
 * The semantic icons take the same props as `FaIcon`, filling in various default values.
 */
var DangerIcon = forwardRef(function (_a, ref) {
    var addClassName = _a.addClassName, iconProps = __rest(_a, ["addClassName"]);
    var intl = useIntl();
    var appearanceColor = "tw-text-risk-fail";
    return (React.createElement(FaIcon, __assign({ "aria-label": intl.formatMessage({ id: "rVKYSV", defaultMessage: [{ type: 0, value: "Danger" }] }) }, iconProps, { addClassName: classNames(addClassName, appearanceColor), faIconDef: faExclamationCircle, ref: ref })));
});
DangerIcon.displayName = "DangerIcon";
var InfoIcon = forwardRef(function (_a, ref) {
    var addClassName = _a.addClassName, iconProps = __rest(_a, ["addClassName"]);
    var intl = useIntl();
    var appearanceColor = "tw-text-link dark:tw-text-blue-400";
    return (React.createElement(FaIcon, __assign({ "aria-label": intl.formatMessage({ id: "PVaBXM", defaultMessage: [{ type: 0, value: "More info" }] }) }, iconProps, { addClassName: classNames(addClassName, appearanceColor), faIconDef: faInfoCircle, ref: ref })));
});
InfoIcon.displayName = "InfoIcon";
var SuccessIcon = forwardRef(function (_a, ref) {
    var addClassName = _a.addClassName, iconProps = __rest(_a, ["addClassName"]);
    var intl = useIntl();
    var appearanceColor = "tw-text-risk-2";
    return (React.createElement(FaIcon, __assign({ "aria-label": intl.formatMessage({ id: "a6vCkm", defaultMessage: [{ type: 0, value: "Success" }] }) }, iconProps, { addClassName: classNames(addClassName, appearanceColor), faIconDef: faCheckCircle, ref: ref })));
});
SuccessIcon.displayName = "SuccessIcon";
var WarningIcon = forwardRef(function (_a, ref) {
    var addClassName = _a.addClassName, iconProps = __rest(_a, ["addClassName"]);
    var intl = useIntl();
    var appearanceColor = "tw-text-orange-400 dark:tw-text-risk-3";
    return (React.createElement(FaIcon, __assign({ "aria-label": intl.formatMessage({ id: "2NNMLm", defaultMessage: [{ type: 0, value: "Warning" }] }) }, iconProps, { addClassName: classNames(addClassName, appearanceColor), faIconDef: faExclamationTriangle, ref: ref })));
});
WarningIcon.displayName = "WarningIcon";
var defaultPropsLoadingIcon = {};
var LoadingIcon = forwardRef(function (_a, ref) {
    var addClassName = _a.addClassName, iconProps = __rest(_a, ["addClassName"]);
    var intl = useIntl();
    return (React.createElement(FaIcon, __assign({ "aria-label": intl.formatMessage({ id: "rVslWc", defaultMessage: [{ type: 0, value: "Loading" }] }) }, iconProps, { addClassName: classNames(addClassName, "tw-animate-spin"), faIconDef: faSpinnerThird, ref: ref })));
});
LoadingIcon.defaultProps = defaultPropsLoadingIcon;
LoadingIcon.displayName = "LoadingIcon";
var defaultPropsLoadingIconFull = {};
function LoadingIconFull(_a) {
    var addClassName = _a.addClassName, iconProps = __rest(_a, ["addClassName"]);
    var intl = useIntl();
    return (React.createElement(FaIcon, __assign({ "aria-label": intl.formatMessage({ id: "m91XfW", defaultMessage: [{ type: 0, value: "Loading" }] }) }, iconProps, { addClassName: classNames(addClassName, "tw-animate-spin"), faIconDef: faCircleNotch })));
}
LoadingIconFull.defaultProps = defaultPropsLoadingIconFull;
LoadingIconFull.displayName = "LoadingIconFull";
var defaultProps = {
    apperance: "secondary"
};
function ProgressRing(props) {
    var radius = props.radius, stroke = props.stroke, progress = props.progress, apperance = props.apperance;
    var normalizedRadius = radius - stroke * 2;
    var circumference = normalizedRadius * 2 * Math.PI;
    var strokeDashoffset = circumference - (progress / 100) * circumference;
    var intl = useIntl();
    var ringStrokeColor = apperance == "secondary" ? "#38A5FF" : "#016FCC";
    return (React.createElement("svg", { "aria-label": intl.formatMessage({ id: "0h33kH", defaultMessage: [{ type: 0, value: "Progress" }] }), height: radius * 1.99, width: radius * 1.99 },
        React.createElement("circle", { stroke: ringStrokeColor, fill: "transparent", strokeWidth: stroke, strokeDasharray: circumference + " " + circumference, style: {
                transform: "rotate(-90deg)",
                transformOrigin: "50% 50%",
                transition: "strokeDashoffset 0.35s"
            }, r: normalizedRadius, cx: radius, cy: radius }),
        React.createElement("circle", { stroke: "currentColor", fill: "transparent", strokeWidth: stroke, strokeDasharray: circumference + " " + circumference, style: {
                transform: "rotate(-90deg)",
                transformOrigin: "50% 50%",
                transition: "strokeDashoffset 0.35s",
                strokeDashoffset: strokeDashoffset,
                zIndex: 10
            }, r: normalizedRadius, cx: radius, cy: radius })));
}
ProgressRing.displayName = "ProgressRing";
ProgressRing.defaultProps = defaultProps;
var defaultPropsIinfoTooltipIcon = {
    size: "sm"
};
var InfoTooltipIcon = forwardRef(function (_a, ref) {
    var addClassName = _a.addClassName, size = _a.size, iconProps = __rest(_a, ["addClassName", "size"]);
    return (React.createElement(FaIcon, __assign({}, iconProps, { ref: ref, addClassName: classNames(addClassName, "tw-text-disabled"), faIconDef: faInfoCircle, size: size })));
});
InfoTooltipIcon.defaultProps = defaultPropsIinfoTooltipIcon;
InfoTooltipIcon.displayName = "InfoTooltipIcon";
var defaultPropsMenuIcon = {
    isOpen: false,
    isSplit: false,
    isBtnGroup: false,
    size: "md",
    buttonType: "default"
};
var MenuIcon = forwardRef(function (_a, ref) {
    var addClassName = _a.addClassName, isOpen = _a.isOpen, isSplit = _a.isSplit, isBtnGroup = _a.isBtnGroup, size = _a.size, buttonType = _a.buttonType, iconProps = __rest(_a, ["addClassName", "isOpen", "isSplit", "isBtnGroup", "size", "buttonType"]);
    var className = !isSplit && size == "sm"
        ? isBtnGroup
            ? "tw-ml-1"
            : "tw-ml-0.5"
        : size == "lg"
            ? "tw-ml-1"
            : size == "md" && "tw-ml-1";
    var iconSize = size == "sm" ? "sm" : "md";
    return (React.createElement("span", { className: classNames("tw-w-5 tw-h-5 tw-grid tw-items-center tw-justify-items-center", buttonType == "form-select" && "tw-absolute tw-right-1.5", className) },
        React.createElement(FaIcon, __assign({}, iconProps, { size: iconSize, faIconDef: faAngleDown, addClassName: classNames("tw-transform tw-transition-transform tw-duration-200", isOpen && "tw-rotate-180", addClassName), ref: ref }))));
});
MenuIcon.defaultProps = defaultPropsMenuIcon;
MenuIcon.displayName = "MenuIcon";

export { DangerIcon, InfoIcon, InfoTooltipIcon, LoadingIcon, LoadingIconFull, MenuIcon, ProgressRing, SuccessIcon, WarningIcon };
//# sourceMappingURL=SemanticIcons.esm.js.map
